import type {
  ColDef,
  ICellRendererParams,
  CellStyleFunc,
  ValueFormatterParams,
  ValueGetterParams,
  GetQuickFilterTextParams,
} from 'ag-grid-community'
import React from 'react'
import { BBDTag } from '@gravitate-js/excalibrr'
import { Button, Tooltip } from 'antd'
import { UnmappedTag } from '../../../components/UnmappedTag'
import { formatVolumeVarianceConsistent } from '../formatters'
import { BillingMethodCellRenderer } from '../components/BillingMethodCellRenderer'
import type { MatchSummaryRow } from '../../../hooks'
import type { ColumnPrecision } from '../../../utils/precisionCalculator'
import { Link } from 'react-router-dom'

// Interface for dynamic currency formatters
export interface CurrencyFormatters {
  formatCurrency: (amount?: number) => string
  formatVariance: (value: number, showSign?: boolean) => string
  formatPerUnit: (amount?: number) => string
  currencySymbol: string
}

// Enhanced interface for consistent formatting
export interface ConsistentCurrencyFormatters extends CurrencyFormatters {
  formatCurrencyConsistent: (amount?: number, precision?: number) => string
  formatVarianceConsistent: (value: number, precision?: number, showSign?: boolean) => string
  formatPerUnitConsistent: (amount?: number, precision?: number) => string
}

export function createMatchSummaryColumnDefs(
  onPerUnitClick?: (priceId: string) => void,
  supplierId?: string,
  supplierName?: string,
  matchStatus?: string,
  currencyFormatters?: ConsistentCurrencyFormatters,
  precisions?: ColumnPrecision,
  onMappingRefresh?: () => void
): ColDef<MatchSummaryRow>[] {
  const columnPrecisions = precisions || { volume: 3, perUnit: 6, currency: 2 }
  const { formatCurrencyConsistent, formatVarianceConsistent, formatPerUnitConsistent, currencySymbol } =
    currencyFormatters || {
      formatCurrency: (amount?: number) => `$${amount?.toFixed(2) || '0.00'}`,
      formatVariance: (value: number, showSign?: boolean) => {
        const sign = showSign && value !== 0 ? (value > 0 ? '+' : '-') : ''
        return `${sign}$${Math.abs(value).toFixed(2)}`
      },
      formatPerUnit: (amount?: number) => `$${amount?.toFixed(4) || '0.0000'}`,
      formatCurrencyConsistent: (amount?: number, precision = 2) =>
        `$${amount?.toFixed(precision) || '0.' + '0'.repeat(precision)}`,
      formatVarianceConsistent: (value: number, precision = 2, showSign?: boolean) => {
        const sign = showSign && value !== 0 ? (value > 0 ? '+' : '-') : ''
        return `${sign}$${Math.abs(value).toFixed(precision)}`
      },
      formatPerUnitConsistent: (amount?: number, precision = 4) =>
        `$${amount?.toFixed(precision) || '0.' + '0'.repeat(precision)}`,
      currencySymbol: '$',
    }

  // Helper function to format per unit with consistent precision
  const formatPerUnitWithVariance = (value: number, isComparison: boolean) => {
    if (isComparison && value !== 0) {
      return formatPerUnitConsistent(value, columnPrecisions.perUnit, currencySymbol, true)
    }
    return formatPerUnitConsistent(value, columnPrecisions.perUnit, currencySymbol, false)
  }
  return [
    {
      colId: 'product', // Stable column identifier
      field: 'product',
      headerName: 'PRODUCT',
      flex: 1, // Restored from old version
      sortable: false,
      filter: 'agSetColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressSelectAll: false,
        suppressMiniFilter: false,
      },
      suppressMovable: true,
      valueGetter: (params: ValueGetterParams<MatchSummaryRow>) => {
        return params.data?.product || ''
      },
      cellRenderer: (params: ICellRendererParams<MatchSummaryRow>) => {
        const row = params.data
        if (!row) return null

        // For comparison/total rows, just show the product name
        if (row.type === 'comparison' || row.type === 'total') {
          return <span style={{ fontWeight: 'bold' }}>{params.value}</span>
        }

        const productName = params.value
        const productObj = row.productObject

        // Check if product is unmapped (has source_name but no internal_id/internal_name)
        // Never show unmapped tags for BOL rows or when match is approved
        const isUnmapped =
          row.source !== 'bol' && productObj?.source_name && (!productObj.internal_id || !productObj.internal_name)
        const isMatchApproved = matchStatus === 'approved'

        if (isUnmapped && !isMatchApproved) {
          return React.createElement(UnmappedTag, {
            type: 'product',
            sourceName: productObj.source_name,
            supplierId: supplierId,
            supplierName: supplierName,
            onMapped: onMappingRefresh,
          })
        }

        return <span style={{ fontWeight: 'bold' }}>{productName}</span>
      },
      cellStyle: ((params: ICellRendererParams<MatchSummaryRow>) => {
        const row = params.data
        if (!row || (row.type !== 'comparison' && row.type !== 'total')) return {}

        // Base background for comparison/total rows (no variance checking for text fields)
        return { background: 'var(--bg-2)' }
      }) as CellStyleFunc<MatchSummaryRow>,
    },
    {
      colId: 'doc_type',
      headerName: 'DOC TYPE',
      flex: 0.5, // Restored from old version
      sortable: false,
      filter: 'agSetColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressSelectAll: false,
        suppressMiniFilter: false,
      },
      suppressMovable: true,
      valueGetter: (params: ValueGetterParams<MatchSummaryRow>) => {
        const row = params.data
        if (!row || row.type === 'comparison' || row.type === 'total') return ''
        const docType = row.source === 'invoice' ? 'INV' : 'BOL'
        return docType
      },
      filterValueGetter: (params: ValueGetterParams<MatchSummaryRow>) => {
        const row = params.data
        if (!row || row.type === 'comparison' || row.type === 'total') return ''
        const docType = row.source === 'invoice' ? 'Invoice' : 'BOL'
        return docType
      },
      getQuickFilterText: (params: GetQuickFilterTextParams<MatchSummaryRow>) => {
        const row = params.data
        if (!row || row.type === 'comparison' || row.type === 'total') return ''
        const docType = row.source === 'invoice' ? 'Invoice' : 'BOL'
        return docType
      },
      cellRenderer: (params: ICellRendererParams<MatchSummaryRow>) => {
        const row = params.data
        if (!row || row.type === 'comparison' || row.type === 'total') {
          return null
        }

        const docType = row.source === 'invoice' ? 'INV' : 'BOL'
        const isInvoice = row.source === 'invoice'

        return React.createElement(BBDTag, {
          theme2: isInvoice,
          theme1: !isInvoice,
          style: { margin: 0, fontSize: '10px' },
          children: docType,
        })
      },
      cellStyle: ((params: ICellRendererParams<MatchSummaryRow>) => {
        const row = params.data
        if (!row || (row.type !== 'comparison' && row.type !== 'total')) return {}

        // Base background for comparison/total rows
        return { background: 'var(--bg-2)' }
      }) as CellStyleFunc<MatchSummaryRow>,
    },
    {
      colId: 'number',
      headerName: 'DOC #',
      flex: 0.8,
      sortable: false,
      filter: 'agTextColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressAndOrCondition: false,
      },
      suppressMovable: true,
      valueGetter: (params: ValueGetterParams<MatchSummaryRow>) => {
        return params.data?.number || ''
      },
      cellRenderer: (params: ICellRendererParams<MatchSummaryRow>) => {
        const row = params.data
        if (!row || row.type === 'comparison' || row.type === 'total') {
          return null
        }

        return <span style={{ fontWeight: 'bold', textTransform: 'uppercase' }}>{params.value}</span>
      },
      cellStyle: ((params: ICellRendererParams<MatchSummaryRow>) => {
        const row = params.data
        if (!row || (row.type !== 'comparison' && row.type !== 'total')) return {}

        // Base background for comparison/total rows (no variance checking for text fields)
        return { background: 'var(--bg-2)' }
      }) as CellStyleFunc<MatchSummaryRow>,
    },
    {
      field: 'bol_number',
      headerName: 'BOL #',
      sortable: false,
      cellRenderer: (params: ICellRendererParams<MatchSummaryRow>) => {
        const { order_number } = params.data || {}
        if (!order_number) return params.value
        return (
          <Link
            to={`/order_movements_page/report?${new URLSearchParams({ order_number })}`}
            target='_blank'
            rel='noopener noreferrer'
          >
            <Button
              color='blue'
              variant='text'
              className='p-0'
              type='link'
              style={{ textDecoration: 'underline', height: '100%' }}
              size='small'
            >
              {params.value}
            </Button>
          </Link>
        )
      },
    },
    {
      colId: 'date', // Stable column identifier - critical for preventing reordering
      field: 'date',
      headerName: 'BOL DATE',
      flex: 1.2, // Restored from old version
      sortable: false,
      filter: 'agDateColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressAndOrCondition: false,
      },
      suppressMovable: true,
      valueGetter: (params: ValueGetterParams<MatchSummaryRow>) => {
        const row = params.data
        if (!row) return ''

        // The date field is already formatted by useMatchSummaryData.formatDateWithTimezone
        // No need to format again here - just return the pre-formatted date string
        return row.date || ''
      },
      cellStyle: ((params: ICellRendererParams<MatchSummaryRow>) => {
        const row = params.data
        if (!row || (row.type !== 'comparison' && row.type !== 'total')) return {}

        // Base background for comparison/total rows (no variance checking for text fields)
        return { background: 'var(--bg-2)' }
      }) as CellStyleFunc<MatchSummaryRow>,
    },
    {
      colId: 'terminal',
      headerName: 'TERMINAL',
      flex: 1.2, // Restored from old version
      sortable: false,
      filter: 'agSetColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressSelectAll: false,
        suppressMiniFilter: false,
      },
      suppressMovable: true,
      valueGetter: (params: ValueGetterParams<MatchSummaryRow>) => {
        return params.data?.terminal || ''
      },
      cellRenderer: (params: ICellRendererParams<MatchSummaryRow>) => {
        const row = params.data
        if (!row || row.type === 'comparison' || row.type === 'total') {
          return params.value || ''
        }

        const terminalName = params.value
        const terminalObj = row.terminalObject

        // Check if terminal is unmapped (has source_name but no internal_id/internal_name)
        // Never show unmapped tags for BOL rows or when match is approved
        const isUnmapped =
          row.source !== 'bol' && terminalObj?.source_name && (!terminalObj.internal_id || !terminalObj.internal_name)
        const isMatchApproved = matchStatus === 'approved'

        if (isUnmapped && !isMatchApproved) {
          return React.createElement(UnmappedTag, {
            type: 'terminal',
            sourceName: terminalObj.source_name,
            supplierId: supplierId,
            supplierName: supplierName,
            onMapped: onMappingRefresh,
          })
        }

        return terminalName
      },
      cellStyle: (params: ICellRendererParams<MatchSummaryRow>) => {
        const row = params.data
        if (!row || (row.type !== 'comparison' && row.type !== 'total')) return {}

        // Base background for comparison/total rows (no variance checking for text fields)
        return { background: 'var(--bg-2)' }
      },
    },
    {
      colId: 'gross_volume',
      headerName: 'GROSS VOL',
      flex: 0.8, // Restored from old version
      type: 'numericColumn',
      sortable: false,
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressAndOrCondition: false,
      },
      suppressMovable: true,
      valueGetter: (params: ValueGetterParams<MatchSummaryRow>) => {
        return params.data?.gross_volume ?? null
      },
      valueFormatter: (params: ValueFormatterParams<MatchSummaryRow>) => {
        const isComparison = params.data?.type === 'comparison'
        const isTotal = params.data?.type === 'total'
        if (isTotal) {
          return formatVolumeVarianceConsistent(params.value, columnPrecisions.volume, true)
        }
        return formatVolumeVarianceConsistent(params.value, columnPrecisions.volume, isComparison)
      },
      cellStyle: (params: ICellRendererParams<MatchSummaryRow>) => {
        const row = params.data
        if (!row || (row.type !== 'comparison' && row.type !== 'total')) return {}

        // Base background for comparison/total rows
        const baseStyle = { background: 'var(--bg-2)' }

        // Show variance warnings for comparison and total rows
        if (row.type === 'comparison' || row.type === 'total') {
          const hasVariance = Math.abs(params.value) > 0.01
          if (hasVariance) {
            return { background: 'var(--theme-warning-dim)' }
          }
        }

        return baseStyle
      },
      cellRenderer: (params: ICellRendererParams<MatchSummaryRow>) => (
        <BillingMethodCellRenderer value={params.value} method={params.data?.billing_method} field='Gross' />
      ),
    },
    {
      colId: 'net_volume',
      headerName: 'NET VOL',
      flex: 0.8, // Restored from old version
      type: 'numericColumn',
      sortable: false,
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressAndOrCondition: false,
      },
      suppressMovable: true,
      valueGetter: (params: ValueGetterParams<MatchSummaryRow>) => {
        return params.data?.net_volume ?? null
      },
      valueFormatter: (params: ValueFormatterParams<MatchSummaryRow>) => {
        const isComparison = params.data?.type === 'comparison'
        const isTotal = params.data?.type === 'total'
        if (isTotal) {
          return formatVolumeVarianceConsistent(params.value, columnPrecisions.volume, true)
        }
        return formatVolumeVarianceConsistent(params.value, columnPrecisions.volume, isComparison)
      },
      cellStyle: (params: ICellRendererParams<MatchSummaryRow>) => {
        const row = params.data
        if (!row || (row.type !== 'comparison' && row.type !== 'total')) return {}

        // Base background for comparison/total rows
        const baseStyle = { background: 'var(--bg-2)' }

        // Show variance warnings for comparison and total rows
        if (row.type === 'comparison' || row.type === 'total') {
          const hasVariance = Math.abs(params.value) > 0.01
          if (hasVariance) {
            return { background: 'var(--theme-warning-dim)' }
          }
        }
        return baseStyle
      },
      cellRenderer: (params: ICellRendererParams<MatchSummaryRow>) => (
        <BillingMethodCellRenderer value={params.value} method={params.data?.billing_method} field='Net' />
      ),
    },
    {
      colId: 'per_unit',
      headerName: 'PER UNIT',
      flex: 0.7, // Restored from old version
      type: 'numericColumn',
      sortable: false,
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressAndOrCondition: false,
      },
      suppressMovable: true,
      valueGetter: (params: ValueGetterParams<MatchSummaryRow>) => {
        return params.data?.per_unit ?? null
      },
      cellRenderer: (params: ICellRendererParams<MatchSummaryRow>) => {
        const row = params.data
        const isComparison = row?.type === 'comparison' || row?.type === 'total'
        const formattedValue = formatPerUnitWithVariance(params.value, isComparison)

        // Check if this is a BOL row with price_id and we have a click handler
        if (row?.source === 'bol' && row?.type === 'transaction' && row?.price_id && onPerUnitClick) {
          return React.createElement(
            'div',
            {
              style: {
                display: 'flex',
                justifyContent: 'flex-end',
                width: '100%',
              },
            },
            React.createElement(
              Tooltip,
              {
                title: 'View Price Breakdown',
                placement: 'top',
              },
              React.createElement(
                'span',
                {
                  style: {
                    color: 'var(--theme-color-2)',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  },
                  onClick: () => {
                    // Use the price_id from the row data
                    if (row.price_id) {
                      onPerUnitClick(row.price_id)
                    }
                  },
                },
                formattedValue
              )
            )
          )
        }

        // Regular display for non-BOL rows
        return formattedValue
      },
      cellStyle: ((params: ICellRendererParams<MatchSummaryRow>) => {
        const row = params.data
        if (!row || (row.type !== 'comparison' && row.type !== 'total')) return {}

        // Base background for comparison/total rows
        const baseStyle = { background: 'var(--bg-2)' }

        // Show variance warnings for comparison and total rows
        if (row.type === 'comparison' || row.type === 'total') {
          const hasVariance = Math.abs(params.value) > 0.01
          if (hasVariance) {
            return { background: 'var(--theme-warning-dim)' }
          }
        }

        return baseStyle
      }) as CellStyleFunc<MatchSummaryRow>,
    },
    {
      colId: 'product_amount',
      headerName: 'PRODUCT AMT',
      flex: 0.9, // Restored from old version
      type: 'numericColumn',
      sortable: false,
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressAndOrCondition: false,
      },
      suppressMovable: true,
      valueGetter: (params: ValueGetterParams<MatchSummaryRow>) => {
        return params.data?.product_amount ?? null
      },
      valueFormatter: (params: ValueFormatterParams<MatchSummaryRow>) => {
        const isComparison = params.data?.type === 'comparison'
        const isTotal = params.data?.type === 'total'
        if (isTotal || isComparison) {
          return formatVarianceConsistent(params.value, columnPrecisions.currency, currencySymbol, true)
        }
        return formatCurrencyConsistent(params.value, columnPrecisions.currency, currencySymbol)
      },
      cellStyle: ((params: ICellRendererParams<MatchSummaryRow>) => {
        const row = params.data
        if (!row || (row.type !== 'comparison' && row.type !== 'total')) return {}

        // Base background for comparison/total rows
        const baseStyle = { background: 'var(--bg-2)' }

        // Show variance warnings for comparison and total rows
        if (row.type === 'comparison' || row.type === 'total') {
          const hasVariance = Math.abs(params.value) > 0.01
          if (hasVariance) {
            return { background: 'var(--theme-warning-dim)' }
          }
        }

        return baseStyle
      }) as CellStyleFunc<MatchSummaryRow>,
    },
    {
      colId: 'tax_amount',
      headerName: 'TAX AMT',
      flex: 0.8, // Restored from old version
      type: 'numericColumn',
      sortable: false,
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressAndOrCondition: false,
      },
      suppressMovable: true,
      valueGetter: (params: ValueGetterParams<MatchSummaryRow>) => {
        return params.data?.tax_amount ?? null
      },
      valueFormatter: (params: ValueFormatterParams<MatchSummaryRow>) => {
        const isComparison = params.data?.type === 'comparison'
        const isTotal = params.data?.type === 'total'
        if (isTotal || isComparison) {
          return formatVarianceConsistent(params.value, columnPrecisions.currency, currencySymbol, true)
        }
        return formatCurrencyConsistent(params.value, columnPrecisions.currency, currencySymbol)
      },
      cellStyle: ((params: ICellRendererParams<MatchSummaryRow>) => {
        const row = params.data
        if (!row || (row.type !== 'comparison' && row.type !== 'total')) return {}

        // Base background for comparison/total rows
        const baseStyle = { background: 'var(--bg-2)' }

        // Show variance warnings for comparison and total rows
        if (row.type === 'comparison' || row.type === 'total') {
          const hasVariance = Math.abs(params.value) > 0.01
          if (hasVariance) {
            return { background: 'var(--theme-warning-dim)' }
          }
        }

        return baseStyle
      }) as CellStyleFunc<MatchSummaryRow>,
    },
    {
      colId: 'total_amount',
      headerName: 'TOTAL AMT',
      flex: 1, // Restored from old version
      type: 'numericColumn',
      pinned: 'right',
      sortable: false,
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressAndOrCondition: false,
      },
      suppressMovable: true,
      valueGetter: (params: ValueGetterParams<MatchSummaryRow>) => {
        return params.data?.total_amount ?? null
      },
      valueFormatter: (params: ValueFormatterParams<MatchSummaryRow>) => {
        const isComparison = params.data?.type === 'comparison'
        const isTotal = params.data?.type === 'total'
        if (isTotal || isComparison) {
          return formatVarianceConsistent(params.value, columnPrecisions.currency, currencySymbol, true)
        }
        return formatCurrencyConsistent(params.value, columnPrecisions.currency, currencySymbol)
      },
      cellStyle: ((params: ICellRendererParams<MatchSummaryRow>) => {
        const row = params.data
        if (!row || (row.type !== 'comparison' && row.type !== 'total')) return {}

        // Base background for comparison/total rows
        const baseStyle = { background: 'var(--bg-2)' }

        // Show variance warnings for comparison and total rows
        if (row.type === 'comparison' || row.type === 'total') {
          const hasVariance = Math.abs(params.value) > 0.01
          if (hasVariance) {
            return { background: 'var(--theme-warning-dim)' }
          }
        }

        return baseStyle
      }) as CellStyleFunc<MatchSummaryRow>,
    },
  ]
}
